
import { defineComponent, ref, watch, computed } from "vue";
import { hideModal } from "@/core/helpers/dom";
import { useStore } from "vuex";
import { Actions } from "@/store/enums/StoreEnums";
import { toRaw } from '@vue/reactivity';

import { ErrorMessage, Field, Form } from "vee-validate";
import * as Yup from "yup";
import yupLocaleTr from 'yup-locale-tr';

import Swal from "sweetalert2/dist/sweetalert2.js";
import { Integration } from "@/dtos/Integration";
import { IntegrationType } from "@/dtos/IntegrationType";
import { TrendyolCredentials } from "@/dtos/Integration";

export default defineComponent({
  name: "create-update-integration",
  components: {
    ErrorMessage,
    Field,
    Form,
  },
  props: {
    selectedIntegration: Object
  },
  emits:["saved"],

  setup(props,ctx) {
    Yup.setLocale(yupLocaleTr);

    const submitButtonRef = ref<null | HTMLButtonElement>(null);
    const modalRef = ref<null | HTMLElement>(null);
    const createAPIKeyModalRef = ref<null | HTMLElement>(null);
    const prpInt = props.selectedIntegration as Integration;
    const apiData = ref<Integration>(prpInt);

    // Yemeksepeti new-platform OTP flow (modal-scoped)
    const ysOtpRequired = ref(false);
    const ysOtpCode = ref();
    const ysFlowId = ref();
    const ysOtpEmail = ref();
    const ysChannelValue = ref();
    const ysAuth = ref();

    const store = useStore();
    const debounce = (func, wait) => {
      let timeout;

      return function executedFunction(...args) {
        const later = () => {
          clearTimeout(timeout);
          func(...args);
        };

        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
      };
    };

    const deepCopy = (obj: any) => {
      if (obj === null || typeof obj !== 'object') {
        return obj;
      }

      const copy = Array.isArray(obj) ? [] : {};

      for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
          copy[key] = deepCopy(obj[key]);
        }
      }

      return copy;
    }
    const changeIntegrationType = (type: number) => {
      apiData.value.integrationType = type;
    };
    const range = (end: number) => {
      return Array.from({ length: end }, (v, k) => k);
    };
    // Required credentials present for the selected platform?
    const canFetchBranches = computed(() => {
      const t = apiData.value?.integrationType;
      const c: any = apiData.value?.credentials || {};
      if (t === IntegrationType.Trendyol) return !!(c.supplierId && c.apiKey && c.apiSecret);
      if (
        t === IntegrationType.Getir ||
        t === IntegrationType.MigrosYemek ||
        t === IntegrationType.PaketServisi
      )
        return !!(c.username && c.password);
      return false;
    });
    const updateBranches = () => {
      // Never attempt a login with incomplete credentials (this used to fire on
      // every keystroke via a watcher and spammed error popups).
      if (apiData.value.integrationType !== IntegrationType.YemekSepeti && !canFetchBranches.value) {
        return;
      }
      switch (apiData.value.integrationType) {
        case IntegrationType.YemekSepeti:
          // New platform requires OTP, so we do NOT auto-trigger login here
          // (that would spam OTP codes). Use the explicit "Bağlan" button
          // -> connectYemeksepeti() instead.
          break;
        case IntegrationType.Getir:
          store.dispatch(Actions.GETIR_CHECK, {
            username: apiData.value.credentials?.username,
            password: apiData.value.credentials?.password,
          }).then((r) => {
            if (r.status == 201) {
              apiData.value.branches = r.data;
            } else {
              Swal.fire({
                title: "Hata",
                text: "Getir kullanıcı adı ve şifre hatalı (Şifre değiştirme zamanınız gelmiş olabilir, getir panelinde çıkış yapıp giriş yapın)",
                icon: "error",
                confirmButtonText: "Tamam"
              });
            }
          });
          break;
        case IntegrationType.MigrosYemek:
          store.dispatch(Actions.MIGROS_CHECK, {
            username: apiData.value.credentials?.username,
            password: apiData.value.credentials?.password,
          }).then((r) => {
            if (r.status == 201) {
              apiData.value.branches = r.data;
            } else {
              Swal.fire({
                title: "Hata",
                text: "Migros kullanıcı adı ve şifre hatalı (Şifre değiştirme zamanınız gelmiş olabilir, migros panelinde çıkış yapıp giriş yapın)",
                icon: "error",
                confirmButtonText: "Tamam"
              });
            }
          });
          break;
        case IntegrationType.PaketServisi:
          store.dispatch(Actions.PAKETSERVISI_CHECK, {
            username: apiData.value.credentials?.username,
            password: apiData.value.credentials?.password,
          }).then((r) => {
            if (r.status == 201) {
              apiData.value.branches = r.data;
            } else {
              Swal.fire({
                title: "Hata",
                text: "Paket Servisi kullanıcı adı ve şifre hatalı",
                icon: "error",
                confirmButtonText: "Tamam"
              });
            }
          });
          break;
        case IntegrationType.Trendyol: {
          const trendyolCreds = apiData.value.credentials as TrendyolCredentials;

          store.dispatch(Actions.TRENDYOL_CHECK, {
            supplierId: trendyolCreds.supplierId,
            apiKey: trendyolCreds.apiKey,
            apiSecret: trendyolCreds.apiSecret,
          }).then((r) => {
            if (r.status == 201) {
              apiData.value.branches = r.data;
            } else {
              Swal.fire({
                title: "Hata",
                text: "Trendyol bilgileri hatalı",
                icon: "error",
                confirmButtonText: "Tamam"
              });
            }
          });
        }
          break;
      }
    };
    // Explicit Yemeksepeti connect (new platform). Triggers OTP if the device
    // is not yet trusted; otherwise returns branches + tokens directly.
    const connectYemeksepeti = () => {
      ysOtpRequired.value = false;
      store
        .dispatch(Actions.YEMEKSEPETI_CHECK, {
          username: apiData.value.credentials?.username,
          password: apiData.value.credentials?.password,
        })
        .then((r: any) => {
          if (r && r.status == 201) {
            if (r.data.otpRequired) {
              ysFlowId.value = r.data.flowId;
              ysOtpEmail.value = r.data.email;
              ysChannelValue.value = r.data.channelValue;
              ysOtpRequired.value = true;
            } else {
              ysAuth.value = r.data.auth;
              apiData.value.branches = r.data.branches;
            }
          } else {
            Swal.fire({
              title: "Hata",
              text: "Yemeksepeti kullanıcı adı ve şifre hatalı",
              icon: "error",
              confirmButtonText: "Tamam",
            });
          }
        });
    };

    const verifyOtpModal = () => {
      store
        .dispatch(Actions.YEMEKSEPETI_VERIFY_OTP, {
          email: ysOtpEmail.value,
          flowId: ysFlowId.value,
          otpCode: ysOtpCode.value,
        })
        .then((r: any) => {
          if (r && r.status == 201) {
            ysAuth.value = r.data.auth;
            apiData.value.branches = r.data.branches;
            ysOtpRequired.value = false;
          } else {
            Swal.fire({
              title: "Hata",
              text: "Doğrulama kodu hatalı veya süresi dolmuş. Lütfen tekrar deneyin.",
              icon: "error",
              confirmButtonText: "Tamam",
            });
          }
        });
    };

    // When opening the modal to edit an existing integration, load its saved
    // credentials and fetch its branches once. Brand-new integrations wait for
    // the explicit "Bağlan" button — no per-keystroke login attempts.
    watch(() => props.selectedIntegration, (newValue) => {
      const rawValue = toRaw(newValue as Integration);
      apiData.value = deepCopy(rawValue);
      updateBranches();
    });

    const validationSchema = Yup.lazy(values => {
      let schema = Yup.object().shape({
        branch: Yup.string().required().label("Şube"),
      });

      if (apiData.value.integrationType != IntegrationType.Trendyol) {
        schema = schema.concat(
          Yup.object().shape({
            userName: Yup.string().required().label("Kullanıcı Adı"),
            password: Yup.string().required().label("Parola"),
          })
        )
      } else if (apiData.value.integrationType == IntegrationType.Trendyol) {
        schema = schema.concat(
          Yup.object().shape({
            apiKey: Yup.string().required().label("Api Key"),
            apiSecret: Yup.string().required().label("Api Secret"),
            supplierId: Yup.string().required().label("Satıcı Id"),
          })
        )
      }

      return schema;
    });

    const submit = () => {

      if (!submitButtonRef.value) {
        return;
      }

      //Disable button
      submitButtonRef.value.disabled = true;
      // Activate indicator
      submitButtonRef.value.setAttribute("data-kt-indicator", "on");
      if(apiData.value.integrationType == IntegrationType.MigrosYemek && apiData.value.credentials){
        apiData.value.credentials.selectedMenuId = apiData.value.branches.find(p=>p.Id == apiData.value.credentials?.selectedBranch).Tag.toString();
      }
      // if apiData.value has isNew property than set it to null
      if(Object.prototype.hasOwnProperty.call(apiData.value,"isNew"))
        delete apiData.value.isNew;
      store.dispatch(Actions.UPDATE_INTEGRATION, apiData.value).then(async (r) => {
        if (submitButtonRef.value) {
          submitButtonRef.value.disabled = false;

          submitButtonRef.value?.removeAttribute("data-kt-indicator");
        }
        if (r&&r.length >0) {
          // Persist the new-platform Yemeksepeti tokens, if just (re)connected.
          if (ysAuth.value) {
            await store.dispatch(Actions.SAVE_YEMEKSEPETI_AUTH, { auth: ysAuth.value });
          }
          ctx.emit("saved");
          Swal.fire({
            text: "Entegrasyon başarı ile güncellendi!",
            icon: "success",
            buttonsStyling: false,
            confirmButtonText: "Tamam!",
            customClass: {
              confirmButton: "btn btn-primary",
            },
          }).then(() => {
            hideModal(createAPIKeyModalRef.value);
          });
        } else {
          Swal.fire({
            title: "Hata",
            text: "Kayıt yapılırken sorun çıktı",
            icon: "error",
            confirmButtonText: "Tamam"
          });
        }
      });

    };

    return {
      apiData,
      validationSchema,
      submit,
      submitButtonRef,
      modalRef,
      createAPIKeyModalRef,
      IntegrationType,
      changeIntegrationType,
      range,
      connectYemeksepeti,
      verifyOtpModal,
      updateBranches,
      canFetchBranches,
      ysOtpRequired,
      ysOtpCode,
      ysChannelValue,
    };
  },
});
